import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { createAndSendExternalInvitation } from '@/domain/external-invitation/actions/create-and-send-external-invitation.action';
import { selectExternalInvitationsForScopes } from '@/domain/external-invitation/external-invitations.selectors';
import { getCategories } from '@/domain/external-invitation/utils/category/get-categories';
import { getScopeFromModeSectorAndCategory } from '@/domain/external-invitation/utils/scopes/get-scope';
import { getScopesFromModeAndSector } from '@/domain/external-invitation/utils/scopes/get-scopes';
import { Sector } from '@/domain/impact/impact.entity';
import { ModelVersionESS } from '@/domain/project/entities/project.entity';
import { editVenueDates } from '@/domain/project-venue-dates/actions/edit-venue-dates.action.ts';
import { VenueDate } from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { VenueDateProperties } from '@/domain/project-venue-dates/entities/project-venue-dates.entity.ts';
import { selectProjectVenueDateById } from '@/domain/project-venue-dates/project-venue-dates.selectors';
import { CloseButton } from '@/ui/components/button/CloseButton';
import { QuizPanelLayout } from '@/ui/components/quiz-panel/QuizPanelLayout';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { language, translate } from '@/ui/i18n';
import { AssignButton } from '@/ui/pages/app/components/quiz-panel/AssignButton';
import { QuizFormSeparator } from '@/ui/pages/app/components/quiz-panel/co2/components/QuizFormSeparator';
import { ImpactQuestionContext } from '@/ui/pages/app/components/quiz-panel/ImpactQuestionContext';
import { InviteSubmitData } from '@/ui/pages/app/components/stakeholders/assign-external/components/FormExternalInvite';
import { ModalAssignExternal } from '@/ui/pages/app/components/stakeholders/ModalAssignExternal';

import { Co2DayQuiz } from './co2-day-quiz/Co2DayQuiz';
import { ESSDayQuiz } from './ess-day-quiz/ESSDayQuiz';
import { essVenueDateQuizLayout } from './ess-day-quiz/venue-date-ess-quiz-layout';
import { VenueDatePreset } from './VenueDatePreset.tsx';

const sector: Sector = 'ARTISTS';

export function DayQuizPanel({
  dateId,
  onClose,
  modelESS,
}: {
  dateId: number;
  onClose: () => void;
  modelESS: ModelVersionESS;
}) {
  const dispatch = useAppDispatch();

  const venueDate = useAppSelector(selectProjectVenueDateById(dateId));

  const [assignExternalModalOpen, setAssignExternalModalOpen] = useState(false);

  const onClickOpenAssign = () => {
    setAssignExternalModalOpen(true);
  };

  const onCloseAssign = () => {
    setAssignExternalModalOpen(false);
  };

  const onChangePreset = (preset: VenueDateProperties) => {
    if (venueDate) {
      const args = {
        venueDate: {
          ...venueDate,
          properties: preset,
        },
      };
      dispatch(editVenueDates(args));
    }
  };

  const onSubmitInvite = async (data: InviteSubmitData) => {
    const scope = getScopeFromModeSectorAndCategory(
      'venue-dates',
      data.sector,
      data.category,
    );
    const params = {
      name: data.name,
      email: data.email,
      limitDate: data.limitDate,
      eventPosition: undefined,
      title: '',
      projectId: venueDate?.id || -1,
      sector: data.sector,
      scope: scope,
    };
    await dispatch(createAndSendExternalInvitation(params));
  };

  const externalCategories = getCategories('venue-date', sector);
  const externalScopes = getScopesFromModeAndSector('venue-dates', sector);
  const externalExistingInvitations = useAppSelector(
    selectExternalInvitationsForScopes(externalScopes),
  );

  return (
    <>
      <ModalAssignExternal
        open={assignExternalModalOpen}
        onClose={onCloseAssign}
        sector={sector}
        categories={externalCategories}
        existingInvitations={externalExistingInvitations}
        onSubmitInvite={onSubmitInvite}
      />
      <ImpactQuestionContext.Provider
        value={{ panelOpen: true, hideCompleted: false }}
      >
        <QuizPanelLayout panelOpen={true} setPanelOpen={onClose}>
          <Stack sx={{ gap: 2 }}>
            <Header
              venueDate={venueDate}
              invitationCount={externalExistingInvitations.length}
              onClickOpenAssign={onClickOpenAssign}
              onClose={onClose}
            />
            <Box id="day-quiz-co2-section">
              <QuizFormSeparator label={'CO2'} />
            </Box>
            <Co2DayQuiz sector={sector} dateId={dateId} />
            <ConfigurationDefaultDatas />
            <Box id="day-quiz-ess-section">
              <QuizFormSeparator
                label={translate('VenueDates.QuizPanel.ess-parity-equality')}
              />
            </Box>
            <ESSDayQuiz
              dateId={dateId}
              essQuizLayout={essVenueDateQuizLayout}
              modelESS={modelESS}
            />
            <Box id="day-quiz-preset-section">
              <QuizFormSeparator
                label={translate('VenueDates.QuizPanel.configuration')}
              />
            </Box>
            <VenueDatePreset
              selectedDatePreset={venueDate?.properties}
              onChangePreset={onChangePreset}
              readonly={false}
            />
          </Stack>
        </QuizPanelLayout>
      </ImpactQuestionContext.Provider>
    </>
  );
}

function Header({
  venueDate,
  invitationCount,
  onClickOpenAssign,
  onClose,
}: {
  venueDate?: VenueDate;
  invitationCount: number;
  onClickOpenAssign: () => void;
  onClose: () => void;
}) {
  const name = venueDate?.name;
  const date = new Date(venueDate?.date || '').toLocaleDateString(language);
  return (
    <Stack direction={'row'} justifyContent={'space-between'}>
      <Stack direction={'row'}>
        <CloseButton sx={{ mr: 1, ml: -2 }} onClick={onClose} />
        <Typography variant="h6">
          {name} - {date}
        </Typography>
      </Stack>
      <AssignButton
        invitationCount={invitationCount}
        onClickOpen={onClickOpenAssign}
      />
    </Stack>
  );
}

function ConfigurationDefaultDatas() {
  return <></>;
}
